import type { WeddingHallConfirmData, CustomerData } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: WeddingHallConfirmData;
  setData: (data: WeddingHallConfirmData) => void;
  customer: CustomerData;
  onGeneratePDF: () => void;
}

export default function WeddingHallConfirmModal({ isOpen, onClose, data, setData, customer, onGeneratePDF }: Props) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">웨딩홀 확인서</h2>
          <div className="flex items-center gap-3">
            <button
              onClick={onGeneratePDF}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm whitespace-nowrap cursor-pointer flex items-center gap-2"
            >
              <i className="ri-file-pdf-line"></i>
              PDF 생성
            </button>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* 발송 기본 정보 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">발송 기본 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">발송일 <span className="text-red-500">*</span></label>
                <input 
                  type="date" 
                  value={data.sendDate}
                  onChange={(e) => setData({...data, sendDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>

          {/* 수신 (웨딩홀 정보) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">수신 (웨딩홀 정보)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">웨딩홀명 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={data.hallName}
                  onChange={(e) => setData({...data, hallName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">담당자명 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={data.hallManager}
                  onChange={(e) => setData({...data, hallManager: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">전화번호 <span className="text-red-500">*</span></label>
                <input 
                  type="text" 
                  value={data.hallPhone}
                  onChange={(e) => setData({...data, hallPhone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">팩스번호</label>
                <input 
                  type="text" 
                  value={data.hallFax}
                  onChange={(e) => setData({...data, hallFax: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>

          {/* 발신 (웨딩플래너 정보) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">발신 (웨딩플래너 정보)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">업체명</label>
                <input 
                  type="text" 
                  value={data.companyName}
                  onChange={(e) => setData({...data, companyName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">담당자명</label>
                <input 
                  type="text" 
                  value={data.companyManager}
                  onChange={(e) => setData({...data, companyManager: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">휴대폰번호</label>
                <input 
                  type="text" 
                  value={data.companyPhone}
                  onChange={(e) => setData({...data, companyPhone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">팩스번호</label>
                <input 
                  type="text" 
                  value={data.companyFax}
                  onChange={(e) => setData({...data, companyFax: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>

          {/* 고객 정보 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">고객 정보</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-600">신랑:</span> <span className="font-medium">{customer.applicationName.split('♥')[0]} / {customer.groomContact}</span></div>
                <div><span className="text-gray-600">신부:</span> <span className="font-medium">{customer.applicationName.split('♥')[1]} / {customer.brideContact}</span></div>
                <div><span className="text-gray-600">예식일:</span> <span className="font-medium">{customer.weddingDateTime}</span></div>
                <div><span className="text-gray-600">예식장소:</span> <span className="font-medium">{customer.weddingLocation}</span></div>
              </div>
            </div>
          </div>

          {/* 웨딩홀 확인 항목 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">웨딩홀 확인 항목</h3>
            <div className="space-y-4">
              {/* 지불보증인원 */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-900 mb-3">지불보증인원 (Minimum Guarantee)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">최초 보증인원 <span className="text-red-500">*</span></label>
                    <input 
                      type="number" 
                      value={data.initialGuarantee}
                      onChange={(e) => setData({...data, initialGuarantee: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="명"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">최종 보증인원 <span className="text-red-500">*</span></label>
                    <input 
                      type="number" 
                      value={data.finalGuarantee}
                      onChange={(e) => setData({...data, finalGuarantee: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="명"
                    />
                  </div>
                </div>
              </div>

              {/* 최종 리베이트 금액 */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-3">최종 리베이트 금액</h4>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">리베이트 금액 <span className="text-red-500">*</span></label>
                  <input 
                    type="number" 
                    value={data.rebateAmount}
                    onChange={(e) => setData({...data, rebateAmount: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="원"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    입력 금액: <span className="font-bold text-green-600">{data.rebateAmount.toLocaleString()}원</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 요청사항 안내 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">요청사항 안내</h3>
            <textarea 
              value={data.notes}
              onChange={(e) => setData({...data, notes: e.target.value})}
              rows={4}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
            />
          </div>

          {/* 플래너 정보 (문의처) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">플래너 정보 (문의처)</h3>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="space-y-2 text-sm">
                <div><span className="text-gray-600">담당 플래너:</span> <span className="font-medium">{data.companyManager}</span></div>
                <div><span className="text-gray-600">연락처:</span> <span className="font-medium">{data.companyPhone}</span></div>
                <div>
                  <label className="block text-gray-600 mb-1">입금 계좌:</label>
                  <input 
                    type="text" 
                    value={data.accountInfo}
                    onChange={(e) => setData({...data, accountInfo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 미리보기 */}
          <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">웨딩홀 확인서 미리보기</h3>
            
            <div className="space-y-4 text-sm">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">웨딩홀 확인서</h2>
                <p className="text-gray-600">발송일: {data.sendDate}</p>
              </div>

              <div className="border-t-2 border-gray-300 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">[수신]</h4>
                    <div className="space-y-1 text-gray-700">
                      <p>웨딩홀명: {data.hallName}</p>
                      <p>담당자: {data.hallManager}</p>
                      <p>전화: {data.hallPhone}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">[발신]</h4>
                    <div className="space-y-1 text-gray-700">
                      <p>업체명: {data.companyName}</p>
                      <p>담당자: {data.companyManager}</p>
                      <p>전화: {data.companyPhone}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t-2 border-gray-300 pt-4">
                <h4 className="font-semibold text-gray-900 mb-2">[고객 정보]</h4>
                <div className="space-y-1 text-gray-700">
                  <p>신랑: {customer.applicationName.split('♥')[0]} / {customer.groomContact}</p>
                  <p>신부: {customer.applicationName.split('♥')[1]} / {customer.brideContact}</p>
                  <p>예식일: {customer.weddingDateTime}</p>
                  <p>예식장소: {customer.weddingLocation}</p>
                </div>
              </div>

              <div className="border-t-2 border-gray-300 pt-4">
                <h4 className="font-semibold text-gray-900 mb-2">[웨딩홀 확인 항목]</h4>
                <div className="space-y-3">
                  <div className="bg-orange-50 p-3 rounded">
                    <p className="font-medium text-orange-900 mb-1">지불보증인원</p>
                    <p className="text-gray-700">최초: {data.initialGuarantee}명</p>
                    <p className="text-gray-700">최종: {data.finalGuarantee}명</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded">
                    <p className="font-medium text-green-900 mb-1">최종 리베이트 금액</p>
                    <p className="text-lg font-bold text-green-700">{data.rebateAmount.toLocaleString()}원</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
          <button 
            onClick={onClose}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
          >
            취소
          </button>
          <button 
            onClick={() => {
              onGeneratePDF();
              alert('웨딩홀 확인서 PDF가 생성되었습니다.');
            }}
            className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium whitespace-nowrap cursor-pointer flex items-center gap-2"
          >
            <i className="ri-file-pdf-line"></i>
            PDF 생성 및 다운로드
          </button>
        </div>
      </div>
    </div>
  );
}